/**
 * 9 周年庆活动 — 学习时长全局后台上报
 * 与新人福利 /api/study/active-time/report 相互独立，不混用：
 * 本端每秒累计、每 30 秒上报「当天累计秒数」（只增不减），页面关闭前同步补传；
 * 服务端返回 continueReport=false（当日 12 小时已封顶）或活动外错误时停止当天上报；
 * 定时器常驻用于北京时间跨天检测，跨天后重建 clientDayId、累计值清零并自动恢复上报；
 * 页面隐藏或窗口失焦（切走别的窗口/标签页、锁屏息屏、系统休眠）时暂停计时与上报，
 * 切走前补传一次，恢复可见/聚焦后继续；息屏/休眠造成的定时器漂移段不计入时长
 */
import { onMounted, onUnmounted } from 'vue'
import { reportAnniversaryActiveTimeApi } from '@/views/home/components/anniversary/api'

const REPORT_INTERVAL = 30 * 1000 // 30 秒上报一次
const STORAGE_PREFIX = 'anniv_active_time_seconds_'

/** 北京时间当天 yyyy-MM-dd（接口按北京时间跨天，跨日后需生成新的 clientDayId） */
const getBjDate = () => {
  const d = new Date(Date.now() + 8 * 3600 * 1000)
  const p = (n: number) => `${n}`.padStart(2, '0')
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`
}

/** 设备标识（复用主站 alpaca_uuid，同端同设备稳定；不要随机生成，避免被当成多个客户端重复累计） */
const getDeviceId = () => {
  const key = 'alpaca_uuid'
  let id = localStorage.getItem(key)
  if (!id) {
    id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
      .replace(/[xy]/g, (c) => {
        const r = (Math.random() * 36) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(36)
      })
      .substring(0, 24)
    localStorage.setItem(key, id)
  }
  return id
}

function getStoredSeconds(date: string): number {
  const raw = localStorage.getItem(`${STORAGE_PREFIX}${date}`)
  if (!raw) return 0
  const n = parseInt(raw, 10)
  return isNaN(n) ? 0 : n
}

function setStoredSeconds(date: string, seconds: number) {
  localStorage.setItem(`${STORAGE_PREFIX}${date}`, String(seconds))
}

export function useAnniversaryActiveTimeReport() {
  let tickTimer: ReturnType<typeof setInterval> | null = null
  let reportTimer: ReturnType<typeof setInterval> | null = null
  let accumulated = 0
  let stopped = false // 停报标记：服务端 continueReport=false（当日封顶）或活动外时置 true
  let paused = false // 暂停标记：页面隐藏/窗口失焦（切走、锁屏息屏）时暂停计时与上报
  let lastTickAt = Date.now() // 上次计时时间戳：识别系统息屏/休眠造成的定时器漂移

  const deviceId = getDeviceId()
  let bjDate = getBjDate()
  let clientDayId = `web:${deviceId}:${bjDate}`

  /**
   * 停止当天计时与上报：仅置停报标记，定时器保留空转用于跨天检测；
   * 若清掉定时器，页面长期不关闭时跨天后将无法自动恢复上报
   */
  function stopAll() {
    stopped = true
  }

  /**
   * 北京时间跨天检测：跨日后重建 clientDayId、按新一天重置累计秒数并复位停报标记，
   * 保证页面跨过零点后从 0 重新累计、当日封顶次日恢复上报
   */
  function checkDayRollover() {
    const today = getBjDate()
    if (today === bjDate) return
    bjDate = today
    clientDayId = `web:${deviceId}:${bjDate}`
    accumulated = getStoredSeconds(bjDate)
    stopped = false
  }

  async function report() {
    // 先做跨天检测：跨天后 clientDayId / 累计值已重建，停报标记复位
    checkDayRollover()

    const token = localStorage.getItem('token')
    if (!token || stopped || paused) return

    try {
      const res = await reportAnniversaryActiveTimeApi({
        clientDayId,
        clientAccumulatedSeconds: accumulated
      })
      setStoredSeconds(bjDate, accumulated)

      // 当日累计已达 12 小时上限：当天停止定时上报（次日新 clientDayId 恢复）
      if (res.data?.continueReport === false) {
        stopAll()
      }
    } catch (e: any) {
      // 活动外：服务端返回「当前不在活动时长统计时间内」，停止上报
      if (typeof e?.msg === 'string' && e.msg.includes('活动时长统计')) {
        stopAll()
      } else {
        console.error('周年时长上报失败', e)
      }
    }
  }

  /** 页面关闭前同步补传（异步请求在卸载时可能被中断） */
  function reportSync() {
    checkDayRollover()

    const token = localStorage.getItem('token')
    if (!token || stopped) return

    try {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', '/api/anniversary/active-time/report', false)
      xhr.setRequestHeader('Content-Type', 'application/json')
      xhr.setRequestHeader('token', token)
      xhr.send(JSON.stringify({ clientDayId, clientAccumulatedSeconds: accumulated }))
      if (xhr.status === 200) {
        const res = JSON.parse(xhr.responseText)
        if (res?.data?.continueReport === false) {
          stopAll()
        }
      }
      setStoredSeconds(bjDate, accumulated)
    } catch (error) {
      console.error('周年时长同步补传失败', error)
    }
  }

  /**
   * 页面隐藏 / 窗口失焦（切走别的窗口、标签页最小化、锁屏息屏、系统休眠）时暂停计时与上报；
   * 切走前先补传一次截至当前的累计时长，恢复可见/聚焦后继续。
   * visibilitychange 与 blur 可能先后触发，paused 判断保证幂等、不重复补传
   */
  function syncActiveState() {
    const active = !document.hidden && document.hasFocus()
    if (active) {
      paused = false
      lastTickAt = Date.now()
    } else if (!paused) {
      // 先补传再置暂停标记：report 检查的是 paused 置位前的状态
      report()
      paused = true
    }
  }

  onMounted(() => {
    // 未登录不上报（上报接口 token 必填）
    if (!localStorage.getItem('token')) return

    // 读取本地当日已累积秒数，作为初始值
    accumulated = getStoredSeconds(bjDate)

    // 初始即处于隐藏/失焦状态（如后台标签页打开）时先暂停，等待聚焦恢复
    paused = document.hidden || !document.hasFocus()

    // 每秒累加（同时做跨天检测，跨天后自动从新一天 0 点开始累计）
    tickTimer = setInterval(() => {
      const now = Date.now()
      const drift = now - lastTickAt
      lastTickAt = now
      checkDayRollover()
      // 单次间隔远超 1 秒：系统息屏/休眠或定时器被深度节流，该段不可信、不计入
      if (drift > 3000) return
      if (!stopped && !paused) accumulated += 1
    }, 1000)

    // 每 30 秒上报一次
    reportTimer = setInterval(report, REPORT_INTERVAL)

    window.addEventListener('beforeunload', reportSync)
    document.addEventListener('visibilitychange', syncActiveState)
    window.addEventListener('blur', syncActiveState)
    window.addEventListener('focus', syncActiveState)
  })

  onUnmounted(() => {
    if (tickTimer) clearInterval(tickTimer)
    if (reportTimer) clearInterval(reportTimer)
    window.removeEventListener('beforeunload', reportSync)
    document.removeEventListener('visibilitychange', syncActiveState)
    window.removeEventListener('blur', syncActiveState)
    window.removeEventListener('focus', syncActiveState)
  })
}
